import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { useToast } from "@/components/ui/use-toast";
import { fetchMaterials, fetchMaterialReferenceCatalog, fetchMaterialSuppliers, saveMaterial, saveMaterialSupplier, setMaterialReferenceActive, fetchMaterialUsages, resolveMaterialUsage, setMaterialUsageActive } from "@/lib/api/manufacturing";

import { defaultMaterialSupplier } from "@/lib/defaultMaterialSupplier";

const emptyMaterialLine = {
  material_id: "",
  supplier_id: "",
  material_supplier_id: "",
  quantity: 1,
  unit: "",
  unit_cost: 0,
  notes: "",
  color_usage: "",
  requires_color: false,
  color_variant_excluded: false,
  solo_si_existe_destino: false,
  excluir_al_duplicar: false,
};

export function materialLabel(material) {
  if (!material) return "";
  return material.code != null && String(material.code).trim() !== ""
    ? `${material.code} · ${material.internal_description}`
    : material.internal_description || "";
}

export function supplierLabel(supplier) {
  if (!supplier) return "";
  return supplier.erp_code ? `${supplier.erp_code} · ${supplier.name}` : supplier.name || "";
}

function normalizeMatchText(value) {
  return String(value || "").trim().toLowerCase();
}

function findMaterialByQuery(query, materials) {
  const needle = normalizeMatchText(query);
  if (!needle) return null;
  return materials.find((material) => {
    const description = normalizeMatchText(material.internal_description);
    const label = normalizeMatchText(materialLabel(material));
    const code = normalizeMatchText(material.code);
    return description === needle || label === needle || code === needle;
  }) || null;
}

function findSupplierByQuery(query, suppliers) {
  const needle = normalizeMatchText(query);
  if (!needle) return null;
  return suppliers.find((supplier) => {
    const name = normalizeMatchText(supplier.name);
    const label = normalizeMatchText(supplierLabel(supplier));
    const code = normalizeMatchText(supplier.erp_code);
    return name === needle || label === needle || code === needle;
  }) || null;
}

function catalogSaveErrorMessage(error, fallback) {
  const message = error?.message || "";
  if (/duplicate|unique/i.test(message)) {
    return "Ya existe esa referencia para el material y proveedor seleccionados.";
  }
  return message || fallback;
}

function lineToFormState(line) {
  if (!line) {
    return {
      materialLine: { ...emptyMaterialLine },
      materialQuery: "",
      supplierQuery: "",
      refQuery: "",
    };
  }
  const supplier = line.material_supplier?.supplier;
  return {
    materialLine: {
      material_id: line.material_id || "",
      supplier_id: line.material_supplier?.supplier_id || supplier?.id || "",
      material_supplier_id: line.material_supplier_id || "",
      quantity: line.quantity ?? 1,
      unit: line.unit || line.material?.default_unit || "",
      unit_cost: line.unit_cost ?? 0,
      notes: line.notes || "",
      color_usage: line.color_usage || "",
      requires_color: line.requires_color === true,
      color_variant_excluded: line.color_variant_excluded === true,
      solo_si_existe_destino: Boolean(line.solo_si_existe_destino),
      excluir_al_duplicar: Boolean(line.excluir_al_duplicar),
    },
    materialQuery: materialLabel(line.material),
    supplierQuery: supplierLabel(supplier),
    refQuery: line.material_supplier?.supplier_ref || "",
  };
}

export function CostSheetMaterialLineDialog({
  open,
  onOpenChange,
  processDescription = "",
  hasProcess = false,
  editingLine = null,
  currentMaterialCount = 0,
  materials = [],
  suppliers = [],
  onMaterialsChange,
  onSave,
  onPreviewChange,
  onDefineColorVariants,
  showDuplicateRule = false,
  duplicateRuleDescription = "Al duplicar hacia esta familia, solo se incorpora este material si ya existe en el mismo proceso del artículo origen. Se aplica con y sin Fijo. Tejido conserva el origen salvo Excluir al duplicar. No afecta al alta de artículos.",
}) {
  const { toast } = useToast();
  const [usages, setUsages] = React.useState([]);
  const [loadingUsages, setLoadingUsages] = React.useState(false);
  const [togglingUsage, setTogglingUsage] = React.useState(null);
  const [showInactiveUsages, setShowInactiveUsages] = React.useState(false);
  const defineColorsAfterSave = React.useRef(false);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [materialLine, setMaterialLine] = React.useState(emptyMaterialLine);
  const [materialQuery, setMaterialQuery] = React.useState("");
  const [supplierQuery, setSupplierQuery] = React.useState("");
  const [refQuery, setRefQuery] = React.useState("");
  const [loadingReferences, setLoadingReferences] = React.useState(false);
  const [materialSuppliers, setMaterialSuppliers] = React.useState([]);
  const [togglingMaterial, setTogglingMaterial] = React.useState(null);
  const [showInactive, setShowInactive] = React.useState(false);
  const [showInactiveReferences, setShowInactiveReferences] = React.useState(false);
  const [togglingReference, setTogglingReference] = React.useState(null);
  const [newMaterialConfirmOpen, setNewMaterialConfirmOpen] = React.useState(false);
  const [savingMaterialLine, setSavingMaterialLine] = React.useState(false);

  React.useEffect(() => {
    onPreviewChange?.(open ? materialLine : null);
  }, [open, materialLine, onPreviewChange]);

  const resetForm = React.useCallback((line = null) => {
    const next = lineToFormState(line);
    setMaterialLine(next.materialLine);
    setMaterialQuery(next.materialQuery);
    setSupplierQuery(next.supplierQuery);
    setRefQuery(next.refQuery);
    setNewMaterialConfirmOpen(false);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    resetForm(editingLine);
  }, [editingLine, open, resetForm]);

  React.useEffect(() => {
    let cancelled = false;
    setMaterialSuppliers([]);
    if (open) {
      setLoadingReferences(true);
      fetchMaterialReferenceCatalog().then((result) => {
        if (cancelled) return;
        if (result.error) {
          toast({ title: "Error al cargar referencias", description: result.error.message, variant: "destructive" });
        }
        setMaterialSuppliers(result.data || []);
        setLoadingReferences(false);
      });
    }
    return () => { cancelled = true; };
  }, [open, toast]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingUsages(true);
    fetchMaterialUsages().then((result) => {
      if (cancelled) return;
      setUsages(result.data || []);
      setLoadingUsages(false);
      if (result.error) toast({ title: "Error al cargar usos", description: result.error.message, variant: "destructive" });
    });
    return () => { cancelled = true; };
  }, [open, toast]);

  const toggleUsage = async (usage) => {
    setTogglingUsage(usage.id);
    try {
      const result = await setMaterialUsageActive(usage.id, usage.is_active === false);
      if (result.error) throw result.error;
      setUsages((rows) => rows.map((row) => row.id === usage.id ? result.data : row));
    } catch (error) {
      toast({ title: "No se pudo cambiar el estado del uso", description: error.message, variant: "destructive" });
    } finally { setTogglingUsage(null); }
  };

  const referenceOptions = materialSuppliers.filter((row) =>
    showInactiveReferences || (row.is_active !== false &&
      (materials.find((material) => material.id === row.material_id) || row.material)?.is_active !== false)
  );

  const toggleReference = async (reference) => {
    setTogglingReference(reference.id);
    try {
      const result = await setMaterialReferenceActive(reference.id, reference.is_active === false);
      if (result.error) throw result.error;
      setMaterialSuppliers((rows) => rows.map((row) => row.id === reference.id ? { ...row, ...result.data } : row));
    } catch (error) {
      toast({ title: "No se pudo cambiar el estado de la referencia", description: error.message, variant: "destructive" });
    } finally {
      setTogglingReference(null);
    }
  };

  const toggleMaterial = async (material) => {
    setTogglingMaterial(material.id);
    try {
      const result = await saveMaterial({ ...material, is_active: material.is_active === false }, material.id);
      if (result.error) throw result.error;
      onMaterialsChange?.((rows) => rows.map((row) => row.id === material.id ? result.data : row));
    } catch (error) {
      toast({ title: "No se pudo cambiar el estado", description: error.message, variant: "destructive" });
    } finally {
      setTogglingMaterial(null);
    }
  };

  const handleDialogOpenChange = (nextOpen) => {
    if (!nextOpen && (newMaterialConfirmOpen || infoOpen || savingMaterialLine)) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) resetForm(null);
  };

  const withDefaultSupplier = (line, reference = refQuery) => {
    if (reference.trim()) return line;
    const link = defaultMaterialSupplier(materialSuppliers, line.material_id, line.supplier_id);
    if (!link) return line;
    setSupplierQuery(supplierLabel(link.supplier || suppliers.find((row) => row.id === link.supplier_id)));
    return { ...line, supplier_id: link.supplier_id, unit_cost: link.purchase_price ?? line.unit_cost,
      material_supplier_id: link.supplier_ref?.trim() ? "" : link.id };
  };

  const handleMaterialTextChange = (text) => {
    setMaterialQuery(text);
    const match = findMaterialByQuery(text, materials);
    if (materialLine.material_id !== (match?.id || "")) setMaterialLine(withDefaultSupplier({
      ...materialLine, material_id: match?.id || "", material_supplier_id: "", unit: match?.default_unit || materialLine.unit,
    }));
  };

  const handleSelectMaterial = (material) => {
    if (!material) return;
    setMaterialQuery(materialLabel(material));
    setMaterialLine(withDefaultSupplier({ ...materialLine, material_id: material.id, material_supplier_id: "", unit: material.default_unit || materialLine.unit }));
  };

  const handleSupplierTextChange = (text) => {
    setSupplierQuery(text);
    const match = findSupplierByQuery(text, suppliers);
    if (materialLine.supplier_id !== (match?.id || "")) setMaterialLine(match ? withDefaultSupplier({
      ...materialLine, supplier_id: match.id, material_supplier_id: "",
    }) : { ...materialLine, supplier_id: "", material_supplier_id: "" });
  };

  const handleSelectSupplier = (supplier) => {
    if (!supplier) return;
    setSupplierQuery(supplierLabel(supplier));
    setMaterialLine(withDefaultSupplier({ ...materialLine, supplier_id: supplier.id, material_supplier_id: "" }));
  };

  const handleRefTextChange = (text) => {
    setRefQuery(text);
    // Typing edits the reference only. Selecting a suggestion explicitly loads its combination.
    setMaterialLine(withDefaultSupplier({ ...materialLine, material_supplier_id: "" }, text));
  };

  const handleSelectRef = (row) => {
    if (!row) return;
    const material = materials.find((item) => item.id === row.material_id) || row.material;
    if (!material) return;
    setMaterialQuery(materialLabel(material));
    onMaterialsChange?.((items) => items.some((item) => item.id === material.id) ? items : [...items, material]);
    setRefQuery(row.supplier_ref || "");
    setSupplierQuery(supplierLabel(row.supplier || suppliers.find((supplier) => supplier.id === row.supplier_id)));
    setMaterialLine((prev) => ({
      ...prev,
      material_id: material.id,
      unit: material.default_unit || "",
      supplier_id: row.supplier_id,
      material_supplier_id: row.id,
      unit_cost: row.purchase_price ?? prev.unit_cost,
    }));
  };

  const persistMaterialLine = async ({ createNewMaterial = false } = {}) => {
    if (savingMaterialLine || togglingMaterial || togglingReference || togglingUsage || loadingUsages || loadingReferences) return;
    const materialText = materialQuery.trim();
    if (!materialText) {
      toast({ title: "Material obligatorio", description: "Selecciona o introduce un material.", variant: "destructive" });
      return;
    }
    if (!hasProcess) {
      toast({ title: "Selecciona un proceso", description: "Los materiales deben pertenecer a un proceso.", variant: "destructive" });
      return;
    }
    if ([materialLine.quantity, materialLine.unit_cost].some((value) => String(value).trim() === "" || !Number.isFinite(Number(value)) || Number(value) < 0)) {
      toast({ title: "Validación", description: "Cantidad y precio deben ser números válidos, iguales o mayores que cero.", variant: "destructive" });
      return;
    }

    const resolvedMaterial = materialLine.material_id
      ? materials.find((item) => item.id === materialLine.material_id) || findMaterialByQuery(materialText, materials)
      : findMaterialByQuery(materialText, materials);

    if (!resolvedMaterial && !createNewMaterial) {
      setNewMaterialConfirmOpen(true);
      return;
    }

    const supplierText = supplierQuery.trim();
    const refText = refQuery.trim();
    const resolvedSupplier = materialLine.supplier_id
      ? suppliers.find((item) => item.id === materialLine.supplier_id) || findSupplierByQuery(supplierText, suppliers)
      : findSupplierByQuery(supplierText, suppliers);

    if (supplierText && !resolvedSupplier) {
      toast({
        title: "Proveedor no válido",
        description: "Selecciona un proveedor existente del listado.",
        variant: "destructive",
      });
      return;
    }
    if (refText && !resolvedSupplier) {
      toast({
        title: "Proveedor obligatorio",
        description: "Para guardar una referencia debes seleccionar un proveedor.",
        variant: "destructive",
      });
      return;
    }

    setSavingMaterialLine(true);
    try {
      let usageName = materialLine.color_usage.trim();
      const unchangedUsage = editingLine && normalizeMatchText(editingLine.color_usage) === normalizeMatchText(usageName);
      if (usageName && !unchangedUsage) {
        const usageResult = await resolveMaterialUsage(usageName);
        if (usageResult.error) throw usageResult.error;
        usageName = usageResult.data.name;
        setUsages((rows) => rows.some((row) => row.id === usageResult.data.id) ? rows : [...rows, usageResult.data]);
      }
      const catalog = await fetchMaterials();
      if (catalog.error) throw catalog.error;
      const currentMaterial = catalog.data.find((row) => row.id === resolvedMaterial?.id);
      if (resolvedMaterial && !currentMaterial) {
        throw new Error("El material ya no existe. Selecciona otro material.");
      }
      let materialId = resolvedMaterial?.id;
      let materialUnit = resolvedMaterial?.default_unit || materialLine.unit || "";
      if (!materialId) {
        const createdMaterial = await saveMaterial({ internal_description: materialText });
        if (createdMaterial.error || !createdMaterial.data?.id) {
          toast({
            title: "Error",
            description: createdMaterial.error?.message || "No se pudo crear el material",
            variant: "destructive",
          });
          return;
        }
        materialId = createdMaterial.data.id;
        materialUnit = createdMaterial.data.default_unit || materialUnit;
        onMaterialsChange?.((prev) => [...prev, createdMaterial.data]);
      }

      let materialSupplierId = materialLine.material_supplier_id || null;
      if (resolvedSupplier) {
        const latestLinksResult = await fetchMaterialSuppliers(materialId);
        if (latestLinksResult.error) throw latestLinksResult.error;
        const latestLinks = latestLinksResult.data || [];
        const matchingLinks = latestLinks.filter(
          (row) =>
            row.supplier_id === resolvedSupplier.id &&
            normalizeMatchText(row.supplier_ref) === normalizeMatchText(refText)
        );
        const existingLink = matchingLinks.find((row) => row.id === materialSupplierId)
          || matchingLinks.find((row) => row.is_active !== false) || matchingLinks[0];
        if (existingLink) {
          materialSupplierId = existingLink.id;
        } else {
          const savedLink = await saveMaterialSupplier({
            material_id: materialId,
            supplier_id: resolvedSupplier.id,
            supplier_ref: refText,
            purchase_price: Number(materialLine.unit_cost || 0),
            is_primary: latestLinks.length === 0,
          });
          if (savedLink.error || !savedLink.data?.id) {
            toast({
              title: "Error",
              description: catalogSaveErrorMessage(savedLink.error, "No se pudo guardar la referencia del proveedor"),
              variant: "destructive",
            });
            return;
          }
          materialSupplierId = savedLink.data.id;

        }
      } else {
        materialSupplierId = null;
      }

      const nextSortOrder = editingLine?.id
        ? editingLine.sort_order
        : editingLine?.sort_order || currentMaterialCount + 1;
      const result = await onSave?.({
        material_id: materialId,
        material_supplier_id: materialSupplierId,
        quantity: materialLine.quantity,
        unit: materialLine.unit || materialUnit,
        waste_pct: 0,
        unit_cost: materialLine.unit_cost,
        sort_order: nextSortOrder,
        notes: materialLine.notes,
        color_usage: usageName,
        requires_color: materialLine.requires_color,
        color_variant_excluded: materialLine.color_variant_excluded,
        ...(showDuplicateRule ? { solo_si_existe_destino: materialLine.solo_si_existe_destino, excluir_al_duplicar: materialLine.excluir_al_duplicar } : {}),
      });
      if (result?.error) {
        toast({
          title: "Error",
          description: catalogSaveErrorMessage(result.error, "No se pudo guardar la línea"),
          variant: "destructive",
        });
        return;
      }
      setNewMaterialConfirmOpen(false);
      onOpenChange?.(false);
      resetForm(null);
      if (defineColorsAfterSave.current) {
        defineColorsAfterSave.current = false;
        onDefineColorVariants?.();
      }
    } catch (error) {
      toast({ title: "No se pudo guardar", description: error.message, variant: "destructive" });
    } finally {
      setSavingMaterialLine(false);
    }
  };

  const submitMaterialLine = async (event) => {
    event.preventDefault();
    defineColorsAfterSave.current = false;
    await persistMaterialLine({ createNewMaterial: false });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6"><DialogTitle>{editingLine?.id ? "Editar material del proceso" : "Añadir material al proceso"}</DialogTitle><Button type="button" variant="outline" size="sm" onClick={() => setInfoOpen(true)}>Info</Button></div>
            <DialogDescription>
              {processDescription
                ? `Proceso: ${processDescription}`
                : "Selecciona un proceso antes de añadir materiales."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Activar o desactivar se guarda inmediatamente y no modifica esta línea. Para conservar los cambios de la línea, pulsa {editingLine?.id ? "Actualizar material" : "Añadir material"}; cerrar con la X los descarta.</p>
          <form onSubmit={submitMaterialLine}>
            <fieldset disabled={savingMaterialLine} className="grid gap-3 lg:grid-cols-12">
            <div className="lg:col-span-12">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Referencia del material del proveedor</label>
              <SearchableCombobox
                value={refQuery}
                onValueChange={handleRefTextChange}
                items={referenceOptions}
                renderItemAction={(row) => (
                  <label className="flex shrink-0 items-center gap-1 px-2 text-xs">
                    <input type="checkbox" checked={row.is_active !== false}
                      aria-label={`Referencia activa: ${row.supplier_ref || "Sin referencia"} · ${supplierLabel(row.supplier)} · ${materialLabel(row.material)}`}
                      disabled={Boolean(togglingReference) || savingMaterialLine}
                      onChange={() => toggleReference(row)} />
                    Activo
                  </label>
                )}
                getItemLabel={(row) => `${row.supplier_ref || "(sin ref.)"} · ${supplierLabel(row.supplier)} · ${materialLabel(row.material)}`}
                selectedId={materialLine.material_supplier_id}
                onSelect={handleSelectRef}
                allowCustom
                placeholder="Escribe primero la referencia del proveedor"
                disabled={!hasProcess}
                emptyMessage={loadingReferences ? "Cargando referencias…" : "Sin referencias coincidentes"}
              />
              <div className="mt-1 text-xs text-muted-foreground">
                <label className="mt-1 flex items-center gap-2">
                  <input type="checkbox" checked={showInactiveReferences} onChange={(event) => setShowInactiveReferences(event.target.checked)} />
                  Mostrar también referencias y materiales inactivos
                </label>
              </div>
            </div>
            <div className="lg:col-span-6">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Material</label>
            <SearchableCombobox
              value={materialQuery}
              onValueChange={handleMaterialTextChange}
              items={materials.filter((row) => showInactive || row.is_active !== false)}
              renderItemAction={(row) => (
                <label className="flex shrink-0 items-center gap-1 px-2 text-xs">
                  <input type="checkbox" checked={row.is_active !== false}
                    aria-label={`Material activo: ${materialLabel(row)}`}
                    disabled={Boolean(togglingMaterial) || savingMaterialLine}
                    onChange={() => toggleMaterial(row)} />
                  Activo
                </label>
              )}
              getItemLabel={materialLabel}
              selectedId={materialLine.material_id}
              onSelect={handleSelectMaterial}
              allowCustom
              placeholder="Material"
              disabled={!hasProcess}
              emptyMessage="Sin materiales coincidentes"
            />
            <div className="text-xs text-muted-foreground">
              <label className="mt-1 flex items-center gap-2">
                <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
                Mostrar también inactivos
              </label>
            </div>
            </div>
            <div className="lg:col-span-6">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Uso del material</label>
              <SearchableCombobox value={materialLine.color_usage} placeholder="Uso del material" allowCustom
                onValueChange={(value) => setMaterialLine((current) => ({ ...current, color_usage: value }))}
                items={usages.filter((row) => showInactiveUsages || row.is_active !== false)} getItemLabel={(row) => row.name}
                selectedId={usages.find((row) => normalizeMatchText(row.name) === normalizeMatchText(materialLine.color_usage))?.id}
                isItemDisabled={(row) => row.is_active === false}
                onSelect={(row) => row.is_active !== false && setMaterialLine((current) => ({ ...current, color_usage: row.name }))}
                disabled={!hasProcess || loadingUsages} emptyMessage={loadingUsages ? "Cargando usos…" : "Sin usos coincidentes"}
                renderItemAction={(row) => <label className="flex shrink-0 items-center gap-1 px-2 text-xs">
                  <input type="checkbox" checked={row.is_active !== false} aria-label={`Uso activo: ${row.name}`}
                    disabled={Boolean(togglingUsage) || savingMaterialLine} onChange={() => toggleUsage(row)} />Activo
                </label>} />
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={showInactiveUsages} onChange={(event) => setShowInactiveUsages(event.target.checked)} />Mostrar también usos inactivos</label>
            </div>
            <div className="lg:col-span-6">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Proveedor</label>
            <SearchableCombobox
              value={supplierQuery}
              onValueChange={handleSupplierTextChange}
              items={suppliers}
              getItemLabel={supplierLabel}
              selectedId={materialLine.supplier_id}
              onSelect={handleSelectSupplier}
              placeholder="Proveedor"
              disabled={!hasProcess}
              emptyMessage="Sin proveedores coincidentes"
            />
            </div>
            <div className="lg:col-span-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Cantidad</label>
              <Input
                type="number"
                min="0"
                step="0.0001"
                disabled={!hasProcess}
                value={materialLine.quantity}
                onChange={(event) => setMaterialLine({ ...materialLine, quantity: event.target.value })}
              />
            </div>
            <div className="lg:col-span-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Precio</label>
              <Input
                type="number"
                min="0"
                step="0.0001"
                disabled={!hasProcess}
                value={materialLine.unit_cost}
                onChange={(event) => setMaterialLine({ ...materialLine, unit_cost: event.target.value })}
              />
            </div>
            <div className="lg:col-span-6">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Descripción/observaciones</label>
              <Input
                disabled={!hasProcess}
                value={materialLine.notes}
                onChange={(event) => setMaterialLine({ ...materialLine, notes: event.target.value })}
              />
            </div>
            <div className="lg:col-span-6">
              <label className="mb-1 block text-xs font-medium">Asignación de color</label>
              {materialLine.color_variant_excluded && <Button type="button" variant="outline" size="sm" className="mb-2"
                onClick={() => setMaterialLine({ ...materialLine, requires_color: true, color_variant_excluded: false })}>
                Volver a mostrar en variantes de color
              </Button>}
              <select aria-label="Asignación de color" className="h-10 w-full rounded border px-2" value={String(materialLine.requires_color === true)}
                onChange={(event) => setMaterialLine({ ...materialLine, requires_color: event.target.value === "true", color_variant_excluded: event.target.value !== "true" })}>
                <option value="true">Requiere color por variante</option><option value="false">No requiere color</option>
              </select>
            </div>
            {showDuplicateRule && (
              <label className="flex items-start gap-2 rounded-md border p-3 text-sm lg:col-span-12">
                <input type="checkbox" className="mt-1" checked={materialLine.solo_si_existe_destino}
                  onChange={(event) => setMaterialLine((current) => ({ ...current, solo_si_existe_destino: event.target.checked }))} />
                <span>Solo si existe en el artículo origen

                </span>
              </label>
            )}
            {showDuplicateRule && (
              <label className="flex items-start gap-2 rounded-md border p-3 text-sm lg:col-span-12">
                <input type="checkbox" className="mt-1" checked={materialLine.excluir_al_duplicar}
                  onChange={(event) => setMaterialLine((current) => ({ ...current, excluir_al_duplicar: event.target.checked }))} />
                <span>Excluir al duplicar

                </span>
              </label>
            )}
            <DialogFooter className="lg:col-span-12 flex-wrap gap-2">
              {onDefineColorVariants && <Button type="button" variant="outline"
                disabled={!hasProcess || savingMaterialLine || loadingReferences || Boolean(togglingMaterial) || Boolean(togglingReference) || Boolean(togglingUsage) || loadingUsages}
                onClick={() => { defineColorsAfterSave.current = true; persistMaterialLine({ createNewMaterial: false }); }}>
                Guardar y definir variantes de color
              </Button>}
              <Button type="button" variant="ghost" onClick={() => handleDialogOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!hasProcess || savingMaterialLine || loadingReferences || Boolean(togglingMaterial) || Boolean(togglingReference) || Boolean(togglingUsage) || loadingUsages}>
                {editingLine?.id ? "Actualizar material" : "Añadir material"}
              </Button>
            </DialogFooter>
            </fieldset>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="z-[80] max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Información de entrada de materiales</DialogTitle><DialogDescription>Referencias, catálogo y colores del material.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Selecciona una referencia para completar material, proveedor y precio. Si es nueva, completa el material y el proveedor.</p>
            <p>Si dejas la referencia vacía, se propone el proveedor y precio del material. Se respeta el proveedor seleccionado y se prioriza la ficha sin referencia; después, el proveedor principal. Puedes ajustar el precio antes de guardar.</p>
            <p>La casilla Activo se guarda al cambiarla. En materiales afecta al catálogo completo; en referencias, a esa combinación de material y proveedor. Conserva los escandallos existentes.</p>
            <p>Busca el uso del material (cuerpo, mangas, cuello…). Si no existe, se da de alta al guardar la línea. Puedes desactivar y reactivar usos desde las coincidencias. Un uso inactivo no se admite en nuevas asignaciones; las líneas existentes lo conservan.</p>
            <p>La asignación de color es «No requiere color» por defecto. Marca «Requiere color por variante» solo para los materiales que deban aparecer en la matriz de colores de la simulación.</p>
            {showDuplicateRule && <><p><b>Solo si existe en el artículo origen:</b> {duplicateRuleDescription}</p><p><b>Excluir al duplicar:</b> omite este material del mismo proceso aunque exista en el artículo origen. Tiene prioridad sobre Fijo, Tejido y la otra casilla. No afecta al alta de artículos.</p></>}
          </div>
          <DialogFooter><Button type="button" onClick={() => setInfoOpen(false)}>Cerrar información</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={newMaterialConfirmOpen} onOpenChange={setNewMaterialConfirmOpen}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Guardar nuevo material?</AlertDialogTitle>
            <AlertDialogDescription>
              El material «{materialQuery.trim()}» no existe. ¿Quieres crearlo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingMaterialLine}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={savingMaterialLine}
              onClick={() => persistMaterialLine({ createNewMaterial: true })}
            >
              Guardar material
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
